import { useEffect, useMemo, useRef } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Dialog as MuiDialog, DialogContent, DialogTitle, Typography, IconButton, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { downloadFile } from 'store/slices/files';
import { DialogFilePreviewContent } from './DialogFilePreviewContent';
import { fileTypeChecks } from 'utils/file-icon';
import { useQuoteAttachments } from 'hooks/useQuoteAttachments';
import { useAsyncDispatch } from 'hooks/useAsyncDispatch';

const Dialog = styled(MuiDialog)(() => ({
  '& > .MuiBackdrop-root': {
    background: 'linear-gradient(75deg, rgba(47, 47, 47, 0.81) 0%, rgba(51, 51, 51, 0.86) 100%)',
  },
  '& > .MuiDialog-container > .MuiPaper-root': {
    backgroundColor: 'transparent',
    maxWidth: '100vw',
    maxHeight: '95vh',
    height: '95vh',
    boxShadow: 'none',
  },
}));

export const DialogNavButton = styled(IconButton)(({ theme }) => ({
  width: 40,
  height: 40,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  color: theme.palette.grey[0],
  zIndex: 2,
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
}));

export const DialogFilePreview = ({
  attachmentPreview = false,
  show = false,
  file = null,
  showPrev,
  showNext,
  close = () => {},
  ...rest
}) => {
  const dispatchAction = useAsyncDispatch();

  const contentRef = useRef();
  const { handleDeleteAttachment } = useQuoteAttachments();

  const isDownloadable = useMemo(() => {
    return (
      !fileTypeChecks.isFileFolder(file) &&
      !fileTypeChecks.isFileQuote(file) &&
      !fileTypeChecks.isFileArtwork(file) &&
      !fileTypeChecks.isFileXml(file)
    );
  }, [file]);

  const handleDownloadFile = (file) => {
    dispatchAction(downloadFile(file), {
      onSuccess: () => {
        enqueueSnackbar(
          <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{`${file.name} download successful.`}</span>
        );
      },
    });
  };

  const handleBackdropClick = (event) => {
    if (contentRef.current && event.target === contentRef.current) {
      close();
    }
  };

  const handleDelete = () => {
    handleDeleteAttachment(attachmentPreview.quoteId, attachmentPreview.attachmentId, close);
  };

  useEffect(() => {
    const handleKeydown = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [close]);

  return (
    <Dialog
      {...rest}
      fullWidth={true}
      open={show}
      onClose={close}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle
        id="alert-dialog-title"
        sx={{ p: 2 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
          >
            <IconButton
              aria-label="close"
              size="small"
              sx={{ color: '#ffffff' }}
              onClick={close}
            >
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 400, fontSize: '16px', color: '#ffffff' }}
            >
              {file?.name}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
          >
            {attachmentPreview?.attachmentId && (
              <IconButton
                aria-label="close"
                size="small"
                sx={{ color: '#ffffff' }}
                onClick={() => handleDelete()}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
            {isDownloadable && (
              <IconButton
                aria-label="close"
                size="small"
                sx={{ color: '#ffffff' }}
                onClick={() => handleDownloadFile(file)}
              >
                <FileDownloadOutlinedIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent
        ref={contentRef}
        onClick={handleBackdropClick}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          padding: 0,
          position: 'relative',
        }}
      >
        {showPrev && (
          <DialogNavButton
            onClick={(e) => {
              e.stopPropagation();
              showPrev();
            }}
            sx={{ left: 0 }}
          >
            <ArrowBackIosOutlinedIcon sx={{ fontSize: 16 }} />
          </DialogNavButton>
        )}

        <DialogFilePreviewContent file={file} />

        {showNext && (
          <DialogNavButton
            onClick={(e) => {
              e.stopPropagation();
              showNext();
            }}
            sx={{ right: 0 }}
          >
            <ArrowForwardIosOutlinedIcon sx={{ fontSize: 16 }} />
          </DialogNavButton>
        )}
      </DialogContent>
    </Dialog>
  );
};
